import type { EmployeeOption } from "../../../../../../../../../../_shared/_logic/form-state"
import {
  buildWorkOrderSelectionFromProjection,
  type WorkOrderSelectSelection,
} from "../../../../../../../../_shared/_logic/work-orders-selection"
import { internalOrderTypeLabel } from "../../../../../../../../../../_shared/_logic/order-type-labels"
import type { WorkOrderFormValue } from "../../../../../../../../_shared/_logic/work-order-form-types"
import { orderTypeSelectionPayload } from "../../../../../../../../_shared/_logic/order-type-selection"
import {
  normalizeDateOnly,
  parseOptionalDurationMinutes,
  parseOptionalNumber,
} from "../../../../../../../../_shared/_logic/work-order-form-utils"

export type AppointmentWorkOrderFormFieldIds = {
  property: string
  propertyUnit: string
  orderNumber: string
  orderType: string
  dueDate: string
  priority: string
  estimatedDuration: string
  allowedEmployees: string
}

export const appointmentWorkOrderFieldIds: AppointmentWorkOrderFormFieldIds = {
  property: "appointment-create-work-order-property",
  propertyUnit: "appointment-create-work-order-property-unit",
  orderNumber: "appointment-create-work-order-number",
  orderType: "appointment-create-work-order-type",
  dueDate: "appointment-create-work-order-due-date",
  priority: "appointment-create-work-order-priority",
  estimatedDuration: "appointment-create-work-order-estimated-duration",
  allowedEmployees: "appointment-create-work-order-allowed-employees",
}

export const normalizeId = (value: unknown) => `${value ?? ""}`.trim()

export const createAppointmentWorkOrderDraft = ({
  defaultOrderNumber,
  employees,
}: {
  defaultOrderNumber?: string | null
  employees: EmployeeOption[]
}): WorkOrderFormValue => ({
  property_id: "",
  property_unit_id: "",
  order_number: defaultOrderNumber ?? "",
  order_type_ids: [],
  custom_order_type: null,
  due_date: "",
  priority: "",
  estimated_duration_min: "",
  allowed_employee_ids: employees.map((employee) => employee.id).filter(Boolean),
})

export const validateAppointmentWorkOrderDraft = ({
  data,
  properties,
  orderTypes,
  formValid,
}: {
  data: WorkOrderFormValue
  properties: Property[]
  orderTypes: OrderType[]
  formValid: boolean
}): string | null => {
  const propertyId = normalizeId(data.property_id)

  if (!propertyId) return "Bitte eine Liegenschaft auswählen."

  const selectedProperty = properties.find((property) => normalizeId(property.id) === propertyId)
  const requiresUnit = Boolean(selectedProperty && (selectedProperty.units?.length ?? 0) > 0)

  if (requiresUnit && !normalizeId(data.property_unit_id)) return "Bitte eine Einheit auswählen."

  const customOrderType = normalizeId(data.custom_order_type?.internal_label)
  const knownIds = new Set(orderTypes.map((type) => normalizeId(type.id)).filter(Boolean))
  const hasKnownOrderType =
    data.order_type_ids.length > 0 && data.order_type_ids.every((id) => knownIds.has(normalizeId(id)))

  if (!hasKnownOrderType && !customOrderType) return "Bitte eine Auftragsart auswählen."

  return formValid ? null : "Bitte die Auftragsdaten prüfen."
}

export const buildWorkOrderCreatePayloadFromDraft = (
  data: WorkOrderFormValue,
): WorkOrderCreatePayload => ({
  property_id: normalizeId(data.property_id),
  property_unit_id: normalizeId(data.property_unit_id) || undefined,
  order_number: normalizeId(data.order_number) || null,
  ...orderTypeSelectionPayload(data),
  due_date: normalizeDateOnly(data.due_date),
  priority: parseOptionalNumber(data.priority),
  estimated_duration_min: parseOptionalDurationMinutes(data.estimated_duration_min),
  allowed_employee_ids: data.allowed_employee_ids?.length ? data.allowed_employee_ids : null,
})

export const buildSelectionForCreatedAppointmentWorkOrder = ({
  workOrder,
  properties,
  scheduleEvents,
}: {
  workOrder: WorkOrder
  properties: Property[]
  scheduleEvents: ScheduleEvent[]
}): WorkOrderSelectSelection =>
  buildWorkOrderSelectionFromProjection({
    workOrder,
    property:
      properties.find(
        (property) => normalizeId(property.id) === normalizeId(workOrder.property_id),
      ) ?? null,
    scheduleEvents,
  })

export const workOrderDraftPropertyUnitType = ({
  data,
  properties,
}: {
  data: WorkOrderFormValue | null | undefined
  properties: Property[]
}): "BUILDING" | "RESIDENTIAL" | null => {
  const property =
    properties.find((entry) => normalizeId(entry.id) === normalizeId(data?.property_id)) ?? null
  const unit = (property?.units ?? []).find(
    (entry) => normalizeId(entry.id) === normalizeId(data?.property_unit_id),
  )

  if (unit?.unit_type === "BUILDING") return "BUILDING"

  if (unit?.unit_type === "RESIDENTIAL") return "RESIDENTIAL"

  return null
}

export const formatOpenWorkOrderWarningLabel = ({
  order,
  properties,
}: {
  order: WorkOrder
  properties: Property[]
}) => {
  const number = normalizeId(order.order_number)
  const orderLabel = number ? `#${number}` : "Ohne Auftragsnummer"
  const typeLabel = normalizeId(internalOrderTypeLabel(order))
  const property = properties.find(
    (entry) => normalizeId(entry.id) === normalizeId(order.property_id),
  )
  const street = [property?.street, property?.house_number]
    .map(normalizeId)
    .filter(Boolean)
    .join(" ")
  const city = normalizeId(property?.city)
  const location = [street, city].filter(Boolean).join(", ")

  return [orderLabel, typeLabel, location].filter(Boolean).join(" • ")
}
